import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// Hand-written, not generated: the api-clipping SDK does not have these endpoints yet (it is
// regenerated from /api-docs outside this repo). Same convention as youtube-source.service.ts,
// so swapping in the generated client later is a drop-in change.

export type RecordingStatus = 'RECORDING' | 'SEGMENTING' | 'UPLOADING' | 'DONE' | 'SHORT' | 'FAILED' | 'SKIPPED';

export type SegmentationStatus = 'RUNNING' | 'DONE' | 'FAILED' | 'SKIPPED';

export type TranscriptionStatus = 'RUNNING' | 'DONE' | 'FAILED' | 'SKIPPED';

export type NewsItemReviewStatus = 'OK' | 'BOUNDARY' | 'MERGED' | 'EXTRA';

// SCHEDULE = RecordingExecutor captured it from the IPTV stream, UPLOAD = received through the
// "Качи снимка" dialog.
export type RecordingSource = 'SCHEDULE' | 'UPLOAD';

export interface RecordingResponse {
	id: string;
	mediumId: string;
	mediumNameCyrillic: string | null;
	mediumNameLatin: string | null;
	scheduleId: string | null;
	source: RecordingSource;
	scheduledStart: string;
	plannedDurationSeconds: number;
	recordedDurationSeconds: number | null;
	status: RecordingStatus;
	failureReason: string | null;
	videoUrl: string | null;
	fileSizeBytes: number | null;
	parts: number;
	startedAt: string | null;
	finishedAt: string | null;
	// Phase 3 - segmentation
	audioUrl: string | null;
	segmentationStatus: SegmentationStatus | null;
	segmentationError: string | null;
	segmentationNotes: string[];
	segmentationModel: string | null;
	segmentedAt: string | null;
	newsStartSeconds: number | null;
	newsEndSeconds: number | null;
	broadcastStatus: string | null;
	trimmedSeconds: number | null;
	itemCount: number;
	reviewedItemCount: number;
	missingItems: number | null;
	reviewedAt: string | null;
	// Phase 4 - transcription
	transcriptionStatus: TranscriptionStatus | null;
	transcriptionError: string | null;
	transcriptionNotes: string[];
	transcriptionModel: string | null;
	transcribedAt: string | null;
}

export interface NewsItemResponse {
	id: string;
	itemNumber: number;
	title: string | null;
	author: string | null;
	startSeconds: number;
	endSeconds: number;
	firstWords: string | null;
	lastWords: string | null;
	confidence: string | null;
	warning: string | null;
	reviewStatus: NewsItemReviewStatus | null;
	reviewNote: string | null;
	// Phase 4 - transcription
	text: string | null;
	originalStartSeconds: number | null;
	originalEndSeconds: number | null;
	// Phase 6 - clip creation
	clipId: string | null;
	// Phase 7 - repeat airings ("Повторени прилози"). duplicateOfNewsItemId set = this item was
	// matched as a repeat; anchorClipId set = the group already has a clip ("🔁 Веќе постои
	// клип"), otherwise link to anchorRecordingId instead ("🔁 Повторено, прв пат: ...").
	duplicateOfNewsItemId: string | null;
	matchPercentage: number | null;
	notDuplicate: boolean;
	anchorRecordingId: string | null;
	anchorClipId: string | null;
	anchorAiredAt: string | null;
}

export type NewsItemClipOutcome = 'CREATED' | 'ALREADY_EXISTS' | 'SKIPPED_EXTRA' | 'NO_TEXT' | 'ERROR';

export interface NewsItemClipResult {
	itemId: string;
	outcome: NewsItemClipOutcome;
	clipId: string | null;
	errorMessage: string | null;
	aiAuthorName: string | null;
	matchedAuthorId: string | null;
	matchedAuthorName: string | null;
	authorAmbiguous: boolean;
}

export interface SegmentationAccuracy {
	reviewedEditions: number;
	ok: number;
	boundary: number;
	merged: number;
	extra: number;
	missing: number;
	realItems: number;
	caughtItems: number;
	itemRecall: number | null;
	exactBoundaries: number | null;
}

export interface RecordingPage {
	content: RecordingResponse[];
	totalElements: number;
	totalPages: number;
	number: number;
	size: number;
}

export interface RecordingSettings {
	maxConcurrentRecordings: number;
	recordingEnabled: boolean;
	segmentationEnabled?: boolean;
	transcriptionEnabled?: boolean;
	autoCreateClipsEnabled?: boolean;
}

export interface ProbeIptvResponse {
	ok: boolean;
	hasVideo: boolean;
	hasAudio: boolean;
	message: string | null;
}

export interface RecordingFilter {
	mediumId?: string | null;
	// Skopje calendar days, YYYY-MM-DD, inclusive
	from?: string | null;
	to?: string | null;
	page?: number;
	size?: number;
}

@Injectable({ providedIn: 'root' })
export class RecordingService {
	private readonly basePath = `${environment.apiClippingUrl}${environment.apiPath}/management`;

	constructor(private http: HttpClient) {}

	getRecordings(filter: RecordingFilter): Observable<RecordingPage> {
		let params = new HttpParams();
		Object.entries(filter).forEach(([key, value]) => {
			if (value !== null && value !== undefined && value !== '') {
				params = params.set(key, String(value));
			}
		});
		return this.http.get<RecordingPage>(`${this.basePath}/recordings`, { params });
	}

	probe(url: string): Observable<ProbeIptvResponse> {
		return this.http.post<ProbeIptvResponse>(`${this.basePath}/recordings/probe`, { url });
	}

	getRecording(id: string): Observable<RecordingResponse> {
		return this.http.get<RecordingResponse>(`${this.basePath}/recordings/${id}`);
	}

	// Deletes the recording row and its news items only - the video/audio stay in B2. The server
	// refuses (400) while the edition is still being produced; see RecordingsComponent.delete.
	deleteRecording(id: string): Observable<void> {
		return this.http.delete<void>(`${this.basePath}/recordings/${id}`);
	}

	getItems(id: string): Observable<NewsItemResponse[]> {
		return this.http.get<NewsItemResponse[]>(`${this.basePath}/recordings/${id}/items`);
	}

	reviewItem(id: string, itemId: string, status: NewsItemReviewStatus | null, note: string | null = null): Observable<NewsItemResponse> {
		return this.http.put<NewsItemResponse>(`${this.basePath}/recordings/${id}/items/${itemId}/review`, { status, note });
	}

	reviewRecording(id: string, missingItems: number | null, done: boolean): Observable<RecordingResponse> {
		return this.http.put<RecordingResponse>(`${this.basePath}/recordings/${id}/review`, { missingItems, done });
	}

	// "Не е повторување" - undoes a repeat-airing match and excludes the item from future matching.
	markNotDuplicate(id: string, itemId: string): Observable<NewsItemResponse> {
		return this.http.put<NewsItemResponse>(`${this.basePath}/recordings/${id}/items/${itemId}/not-duplicate`, {});
	}

	// Ѓ1 - boundary edit from "Провери". If the item already has a clip, the server moves the
	// clip's own boundaries with it in the same transaction (see NewsItemBoundarySync).
	updateItemBoundaries(id: string, itemId: string, startSeconds: number, endSeconds: number): Observable<NewsItemResponse> {
		return this.http.put<NewsItemResponse>(`${this.basePath}/recordings/${id}/items/${itemId}/boundaries`, { startSeconds, endSeconds });
	}

	// Е1 "Раздели" - splits one item into 2-5 parts. Only allowed while the item has no clip yet.
	// Returns every item of the edition (itemNumber is renumbered across all of them by time order).
	splitItem(id: string, itemId: string, splitPointsSeconds: number[], newTitles: string[]): Observable<NewsItemResponse[]> {
		return this.http.post<NewsItemResponse[]>(`${this.basePath}/recordings/${id}/items/${itemId}/split`, { splitPointsSeconds, newTitles });
	}

	// "Креирај клипови" - one clip per item id, through the same service/validation a manually
	// created clip in Клипови goes through. withoutText=true only after the user confirmed
	// "Креирај без текст?" - an item that already has text always uses it regardless of this flag.
	createClipsFromItems(id: string, itemIds: string[], withoutText: boolean): Observable<NewsItemClipResult[]> {
		return this.http.post<NewsItemClipResult[]>(`${this.basePath}/recordings/${id}/items/clips`, { itemIds, withoutText });
	}

	resegment(id: string): Observable<RecordingResponse> {
		return this.http.post<RecordingResponse>(`${this.basePath}/recordings/${id}/segment`, {});
	}

	// "Повтори транскрипција" - re-transcribes from the stored (or re-derived) audio, redistributed
	// onto the edition's current items without touching their boundaries.
	retranscribe(id: string): Observable<RecordingResponse> {
		return this.http.post<RecordingResponse>(`${this.basePath}/recordings/${id}/transcribe`, {});
	}

	getAccuracy(filter: { mediumId?: string | null; from?: string | null; to?: string | null }): Observable<SegmentationAccuracy> {
		let params = new HttpParams();
		Object.entries(filter).forEach(([key, value]) => {
			if (value) {
				params = params.set(key, String(value));
			}
		});
		return this.http.get<SegmentationAccuracy>(`${this.basePath}/recordings/accuracy`, { params });
	}

	// The browser has already PUT the file straight to B2 via the existing presigned-upload
	// mechanism (ManagementUploadControllerService.generatePreSignedUrls, type VIDEO) - this just
	// tells clipping where to find it. mediumId must be a TV medium (the server rejects anything
	// else). scheduledStart is the Skopje date/time the edition aired, "YYYY-MM-DDTHH:mm".
	uploadRecording(mediumId: string, scheduledStart: string, videoUrl: string): Observable<RecordingResponse> {
		return this.http.post<RecordingResponse>(`${this.basePath}/recordings/upload`, { mediumId, scheduledStart, videoUrl });
	}

	// З1 "Прикачи преку линк" (YouTube etc) - the server downloads the video itself (yt-dlp);
	// videoUrl here is the SOURCE link, never a B2 key.
	uploadRecordingFromLink(mediumId: string, scheduledStart: string, videoUrl: string): Observable<RecordingResponse> {
		return this.http.post<RecordingResponse>(`${this.basePath}/recordings/upload-link`, { mediumId, scheduledStart, videoUrl });
	}

	// Д1 "Уреди" - medium/scheduledStart correction. The server refuses (400) if the edition
	// already has clips or is still processing; see RecordingsComponent.openEditDialog.
	updateRecording(id: string, mediumId: string, scheduledStart: string): Observable<RecordingResponse> {
		return this.http.put<RecordingResponse>(`${this.basePath}/recordings/${id}`, { mediumId, scheduledStart });
	}

	getSettings(): Observable<RecordingSettings> {
		return this.http.get<RecordingSettings>(`${this.basePath}/recording-settings`);
	}

	updateSettings(body: RecordingSettings): Observable<RecordingSettings> {
		return this.http.put<RecordingSettings>(`${this.basePath}/recording-settings`, body);
	}
}
